// src/pages/MyOKRs.js

import React, { useEffect, useState, useContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaPlus } from 'react-icons/fa'
import './MyOKRs.css'
import '../styles/okr-m3-tokens.css'
import { authFetch } from '../utils/api'
import { AuthContext } from '../context/AuthContext'
import { calculateOKRStatus } from '../utils/okrStatusUtils';
import PeriodNavigation from '../components/okr/PeriodNavigation'
import MetricsBar from '../components/okr/MetricsBar'
import StatusFilterChips from '../components/okr/StatusFilterChips'
import ObjectiveCard from '../components/okr/ObjectiveCard'

export default function MyOKRs() {
  const { token, user } = useContext(AuthContext)
  const navigate        = useNavigate()

  const isStandard      = user?.role === 'standard'
  const [okrs, setOkrs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [isQuarterly, setIsQuarterly] = useState(true)

  const [expandedMap, setExpandedMap] = useState({})
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [currentQuarter, setCurrentQuarter] = useState('Q1')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  // fetch OKRs whenever quarter/year or view toggles
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    async function fetchOKRs() {
      try {
        // Fetch all OKRs, then filter in frontend
        const res = await authFetch('/api/okrs', token);
        if (!res.ok) {
          const errorText = await res.text().catch(() => 'Unknown error');
          throw new Error(`Failed to fetch OKRs: ${res.status} - ${errorText}`);
        }
        const data = await res.json();
        
        // Ensure we have an array
        if (!Array.isArray(data)) {
          console.error('Expected array but got:', typeof data, data);
          setOkrs([]);
          setLoading(false);
          return;
        }
        
        let filtered = data;
        if (isQuarterly) {
          filtered = data.filter(okr => {
            const matchesType = okr.type === 'quarter';
            const matchesQuarter = okr.quarter && okr.quarter.startsWith(currentQuarter);
            const matchesYear = String(okr.year) === String(selectedYear);
            return matchesType && matchesQuarter && matchesYear;
          });
        } else {
          filtered = data.filter(okr => {
            const matchesType = okr.type === 'annual';
            const matchesYear = String(okr.year) === String(selectedYear);
            return matchesType && matchesYear;
          });
        }
        setOkrs(filtered);
        // init expand state
        const init = {};
        filtered.forEach(o => { init[o._id] = false; });
        setExpandedMap(init);
      } catch (e) {
        console.error('Error fetching OKRs:', e);
        setError(e.message || 'Failed to load OKRs');
        setOkrs([]);
      } finally {
        setLoading(false);
      }
    }
    fetchOKRs();
  }, [token, isQuarterly, currentQuarter, selectedYear])
  
  const handleQuarterChange = (dir) => {
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const idx = quarters.indexOf(currentQuarter);
    let newIdx = dir === 'next'
      ? (idx + 1) % quarters.length
      : (idx + quarters.length - 1) % quarters.length;
    
    if (dir === 'next' && newIdx === 0) {
      setSelectedYear(y => y + 1);
    }
    if (dir === 'prev' && idx === 0) {
      setSelectedYear(y => y - 1);
    }
    
    setCurrentQuarter(quarters[newIdx]);
  };

  // checkbox toggle
  const handleToggleKR = async (id, idx) => {
    const target = okrs.find(o => o._id === id);
    if (!target || !target.keyResults || !target.keyResults[idx]) {
      console.error('❌ [MyOKRs] Invalid OKR or key result index:', { id, idx, target: !!target });
      return;
    }
    
    // Toggle the completed status
    const currentCompleted = target.keyResults[idx].completed || false;
    const newCompleted = !currentCompleted;
    
    console.log('🔄 [MyOKRs] Toggling KR:', { 
      id, 
      idx, 
      current: currentCompleted, 
      new: newCompleted,
      krText: target.keyResults[idx].text?.substring(0, 30)
    });
    
    // Create new keyResults array with updated completed status
    const krs = target.keyResults.map((kr, i) => {
      if (i === idx) {
        return {
          text: kr.text || '',
          team: kr.team || undefined,
          krOwner: kr.krOwner || undefined,
          completed: newCompleted, // Explicit boolean
          deadline: kr.deadline || undefined
        };
      }
      return {
        text: kr.text || '',
        team: kr.team || undefined,
        krOwner: kr.krOwner || undefined,
        completed: kr.completed === true || kr.completed === 'true' || false, // Ensure boolean
        deadline: kr.deadline || undefined
      };
    });
    
    const total = krs.length;
    const done = krs.filter(kr => kr.completed === true).length;
    const progress = total ? Math.round((done / total) * 100) : 0;
    const status = calculateOKRStatus(krs, target.deadline);
    
    // Prepare payload - only send necessary fields
    const payload = {
      objective: target.objective,
      deadline: target.deadline,
      level: target.level,
      owners: target.owners || [target.owner].filter(Boolean),
      owner: target.owner,
      year: target.year,
      quarter: target.quarter,
      type: target.type,
      keyResults: krs,
      progress,
      score: target.score || 0,
      status
    };
    
    console.log('📤 [MyOKRs] Sending payload:', {
      id,
      keyResultsCount: payload.keyResults.length,
      completedCount: payload.keyResults.filter(kr => kr.completed).length,
      progress: payload.progress
    });
    
    // Optimistically update UI
    setOkrs(prev => prev.map(o => o._id === id ? { ...target, ...payload } : o));
    
    try {
      const res = await authFetch(
        `/api/okrs/${id}`,
        token,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      if (res.ok) {
        // Update with server response to ensure we have the saved data
        try {
          const savedOKR = await res.json();
          console.log('✅ [MyOKRs] Saved OKR received:', {
            id: savedOKR._id,
            keyResults: savedOKR.keyResults?.map((kr, i) => ({
              idx: i,
              text: kr.text?.substring(0, 20),
              completed: kr.completed,
              type: typeof kr.completed
            }))
          });
          setOkrs(prev => prev.map(o => {
            if (o._id === id) {
              return savedOKR;
            }
            return o;
          }));
        } catch (parseError) {
          console.error('❌ [MyOKRs] Error parsing saved OKR response:', parseError);
          // Keep the optimistic update if parsing fails
        }
      } else {
        // Revert on failure
        setOkrs(prev => prev.map(o => o._id === id ? target : o));
        const errorText = await res.text().catch(() => 'Unknown error');
        console.error('❌ [MyOKRs] Failed to update OKR:', res.status, errorText);
        alert('Failed to save changes. Please try again.');
      }
    } catch (e) {
      // Revert on error
      setOkrs(prev => prev.map(o => o._id === id ? target : o));
      console.error('❌ [MyOKRs] Failed to update OKR:', e);
      alert('Failed to save changes. Please try again.');
    }
  }

  // delete
  const handleDeleteOKR = async id => {
    console.log('Delete button clicked for OKR:', id);
    try {
      console.log('Token:', token);
      if (!token) {
        alert('No authentication token found. Please log in again.');
        return;
      }
      const res = await authFetch(`/api/okrs/${id}`, token, { method: 'DELETE' })
      const text = await res.text();
      console.log('DELETE response:', res.status, res.statusText, 'Body:', text);
      if (res.ok) {
        setOkrs(prev => prev.filter(o => o._id !== id))
        console.log('OKR deleted from UI');
      } else {
        alert(`Failed to delete OKR: ${res.status} ${res.statusText}\n${text}`);
        console.error('Failed to delete OKR:', text);
      }
    } catch (e) {
      alert('Failed to delete OKR.');
      console.error('Delete error:', e);
    }
  }

  // expand / collapse controls
  const toggleExpand = id =>
    setExpandedMap(m => ({ ...m, [id]: !m[id] }))
  const expandAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, true])))
  const collapseAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, false])))

  // overall stats
  const { totalOkrs, totalKRs, overallProgress, overallScore } = useMemo(() => {
    const totalOkrs = okrs.length
    let totalKRs = 0, done = 0, sum = 0
    okrs.forEach(o => {
      totalKRs += o.keyResults.length
      done     += o.keyResults.filter(kr => kr.completed).length
      sum      += o.score || 0
    })
    const overallProgress = totalKRs ? Math.round((done / totalKRs) * 100) : 0
    const overallScore    = totalOkrs ? sum / totalOkrs : 0
    return { totalOkrs, totalKRs, overallProgress, overallScore }
  }, [okrs])
  
  const okrsWithStatus = useMemo(() => {
    const withStatus = okrs.map(okr => ({
      ...okr,
      status: calculateOKRStatus(okr.keyResults || [], okr.deadline)
    }));
    
    if (selectedStatuses.length === 0) return withStatus;
    return withStatus.filter(okr => selectedStatuses.includes(okr.status));
  }, [okrs, selectedStatuses]);
  
  const handleToggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };
  
  const handleEditOKR = (id) => {
    navigate(`/editokr/${id}`);
  };
  
  const handleCreateOKR = () => {
    navigate('/createokr', { state: { type: isQuarterly ? 'quarter' : 'annual' } });
  };

  if (loading) return <div className="m3-center-message">Loading…</div>
  if (error)   return <div className="m3-center-message m3-error">Error: {error}</div>

  return (
    <div className="m3-my-okrs-page">
      <div className="m3-page-header">
        <h1 className="m3-page-title">My OKRs</h1>
      </div>

      {/* Period Navigation with Create Button */}
      <PeriodNavigation
        year={selectedYear}
        quarter={currentQuarter}
        isQuarterly={isQuarterly}
        onYearChange={setSelectedYear}
        onQuarterChange={setCurrentQuarter}
        onPrev={() => handleQuarterChange('prev')}
        onNext={() => handleQuarterChange('next')}
        onToggleType={setIsQuarterly}
        onCreateOKR={!isStandard ? handleCreateOKR : null}
        showCreateButton={!isStandard}
      />

      {/* Metrics Bar */}
      <MetricsBar
        progress={overallProgress}
        score={overallScore}
        objectives={totalOkrs}
        keyResults={totalKRs}
      />

      {/* Status Filter Chips */}
      <StatusFilterChips
        selectedStatuses={selectedStatuses}
        onToggleStatus={handleToggleStatus}
      />

      {/* Expand/Collapse Controls */}
      {okrs.length > 0 && (
        <div className="m3-expand-controls">
          <button className="m3-button m3-button-text" onClick={expandAll}>
            Expand All
          </button>
          <button className="m3-button m3-button-text" onClick={collapseAll}>
            Collapse All
          </button>
        </div>
      )}

      {/* OKRs List */}
      <section className="m3-okrs-content">
        {okrsWithStatus.length === 0 ? (
          <div className="m3-empty-state">
            <p>No OKRs found{selectedStatuses.length > 0 ? ' matching selected filters' : ''}.</p>
            {!isStandard && selectedStatuses.length === 0 && (
              <button className="m3-button m3-button-filled" onClick={handleCreateOKR}>
                <FaPlus /> Create OKR
              </button>
            )}
          </div>
        ) : (
          <div className="m3-okrs-list">
            {okrsWithStatus.map(okr => {
              const isOwner = Array.isArray(okr.owners) 
                ? okr.owners.includes(user?.name)
                : okr.owner === user?.name;
              
              return (
                <ObjectiveCard
                  key={okr._id}
                  okr={okr}
                  isExpanded={expandedMap[okr._id] || false}
                  onToggleExpand={toggleExpand}
                  onToggleKR={isStandard && !isOwner ? null : handleToggleKR}
                  onEdit={!isStandard ? handleEditOKR : null}
                  onDelete={!isStandard ? handleDeleteOKR : null}
                  canEdit={!isStandard}
                  canDelete={!isStandard}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  )
}
